import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { useLiveUpdate } from "@/contexts/LiveUpdateContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, GripVertical, Calendar, User, Lock, AlertTriangle, Search, Clock } from "lucide-react";
import { toast } from "sonner";
import TaskDialog from "@/components/TaskDialog";

const COLUMNS = [
  { id: "todo", label: "To Do", color: "border-slate-300 dark:border-slate-700", bg: "bg-slate-50/30 dark:bg-slate-900/30", dot: "bg-slate-500" },
  { id: "in_progress", label: "In Progress", color: "border-blue-300 dark:border-blue-800", bg: "bg-blue-50/30 dark:bg-blue-900/10", dot: "bg-blue-500" },
  { id: "review", label: "Review", color: "border-amber-300 dark:border-amber-800", bg: "bg-amber-50/30 dark:bg-amber-900/10", dot: "bg-amber-500" },
  { id: "done", label: "Selesai", color: "border-emerald-300 dark:border-emerald-800", bg: "bg-emerald-50/30 dark:bg-emerald-900/10", dot: "bg-emerald-500" },
];

const PRIORITY_BORDER = { high: "border-l-red-500", medium: "border-l-orange-400", low: "border-l-blue-400" };
const PRIORITY_STYLES = {
  high: "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  medium: "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
  low: "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
};

export default function KanbanPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterDivision, setFilterDivision] = useState(user?.role === "ka_div" ? (user?.division || "all") : "all");
  const { updateTrigger } = useLiveUpdate();

  const fetchData = useCallback(async () => {
    try {
      let url = "/tasks";
      const params = new URLSearchParams();
      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }
      if (filterDivision !== "all") {
        params.append("division", filterDivision);
      }
      params.append("limit", "1000");
      params.append("hide_old_done", "true");

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const [tasksRes, usersRes, projectsRes] = await Promise.all([
        API.get(url), 
        API.get("/users"),
        API.get("/projects")
      ]);
      const fetchedTasks = tasksRes.data.data || tasksRes.data || [];
      setTasks(fetchedTasks);
      setUsers(usersRes.data);
      setProjects((projectsRes.data || []).map(p => p.name));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterDivision]);

  useEffect(() => { fetchData(); }, [fetchData, updateTrigger]);

  const getUserName = (id) => users.find((u) => u.id === id)?.name || "Belum Ditugaskan";

  const getTasksByStatus = (status) => {
    return tasks.filter((t) => {
      const matchStatus = t.status === status;
      const matchSearch = (t.title?.toLowerCase() || "").includes(searchQuery.toLowerCase()) || 
                          (t.description?.toLowerCase() || "").includes(searchQuery.toLowerCase());
      const matchProject = filterProject === "all" || t.project === filterProject;
      
      let matchAssignee = true;
      if (filterAssignee !== "all") {
        if (filterAssignee === "unassigned") {
          matchAssignee = !t.assignees || t.assignees.length === 0;
        } else {
          matchAssignee = t.assignees && t.assignees.some(a => a.id === filterAssignee);
        }
      }
      
      return matchStatus && matchSearch && matchProject && matchAssignee;
    });
  };

  // Manager can drag all tasks, developer can only drag tasks assigned to them
  const canDrag = (task) => ["superadmin", "ka_div", "kepala_tik", "kapokja"].includes(user?.role) || (task.assignees && task.assignees.some(a => a.id === user?.id));

  const isOverdue = (task) => {
    if (!task.deadline || task.status === "done") return false;
    return new Date(task.deadline) < new Date(new Date().toDateString());
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;

    const newStatus = destination.droppableId;
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === draggableId ? { ...t, status: newStatus } : t)));

    try {
      await API.put(`/tasks/${draggableId}/status`, { status: newStatus });
      toast.success("Status tugas diperbarui");
    } catch {
      toast.error("Gagal memperbarui status");
      fetchData(); // Revert
    }
  };

  const openCreate = () => {
    setEditTask(null);
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setEditTask(task);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="kanban-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Papan Kanban
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {["superadmin", "ka_div", "kepala_tik", "kapokja"].includes(user?.role)
              ? "Tarik dan letakkan tugas untuk memperbarui statusnya"
              : "Tarik dan letakkan tugas yang diberikan kepada Anda untuk memperbarui status"}
          </p>
        </div>
        
        <div className="flex gap-3">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 dark:text-slate-500" />
            <Input
              placeholder="Cari tugas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 dark:text-slate-200 dark:placeholder-slate-400 shadow-sm"
            />
          </div>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9">
              <SelectValue placeholder="Proyek" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
              <SelectItem value="all">Semua Proyek</SelectItem>
              {projects.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterAssignee} onValueChange={setFilterAssignee}>
            <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9">
              <SelectValue placeholder="Penugasan" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
              <SelectItem value="all">Semua Anggota</SelectItem>
              <SelectItem value="unassigned">Belum Ditugaskan</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterDivision} onValueChange={setFilterDivision}>
            <SelectTrigger className="w-[160px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9">
              <SelectValue placeholder="Divisi" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
              <SelectItem value="all">Semua Divisi</SelectItem>
              <SelectItem value="sistem_informasi">Sistem Informasi</SelectItem>
              <SelectItem value="infrastruktur_ti">Infrastruktur TI</SelectItem>
              <SelectItem value="pelayanan">Pelayanan</SelectItem>
              <SelectItem value="tata_kelola">Tata Kelola</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600" data-testid="kanban-create-task">
            <Plus className="w-4 h-4 mr-2" /> Tugas Baru
          </Button>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
          {COLUMNS.map((col) => {
            const columnTasks = getTasksByStatus(col.id);
            return (
              <div key={col.id} className={`rounded-lg border ${col.color} ${col.bg} p-3 min-h-[400px]`} data-testid={`kanban-column-${col.id}`}>
                {/* Column header */}
                <div className="flex items-center justify-between mb-4 px-1 border-b border-transparent dark:border-transparent">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {col.label}
                    </h3>
                  </div>
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-white/80 dark:bg-slate-800/80 px-2 py-0.5 rounded-full">
                    {columnTasks.length}
                  </span>
                </div>

                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`space-y-3 min-h-[200px] rounded-md transition-colors ${
                        snapshot.isDraggingOver ? "bg-white/50 dark:bg-slate-800/50" : ""
                      }`}
                    >
                      {columnTasks.map((task, index) => {
                        const draggable = canDrag(task);
                        return (
                        <Draggable key={task.id} draggableId={task.id} index={index} isDragDisabled={!draggable}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-sm border-l-4 ${PRIORITY_BORDER[task.priority]} p-3 transition-all duration-150 ${
                                draggable ? "cursor-pointer hover:shadow-md" : "opacity-70 cursor-default"
                              } ${snapshot.isDragging ? "kanban-card-dragging shadow-xl" : ""}`}
                              onClick={() => openEdit(task)}
                              data-testid={`kanban-card-${task.id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 leading-snug">{task.title}</h4>
                                  {task.project && (
                                    <span className="inline-block mt-1 text-[10px] font-semibold bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 rounded">
                                      {task.project}
                                    </span>
                                  )}
                                </div>
                                {draggable ? (
                                  <div {...provided.dragHandleProps} className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 shrink-0 mt-0.5">
                                    <GripVertical className="w-4 h-4" />
                                  </div>
                                ) : (
                                  <div {...provided.dragHandleProps} className="shrink-0 mt-0.5">
                                    <Lock className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />
                                  </div>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 line-clamp-2">{task.description}</p>
                              )}
                              <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex flex-col gap-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <User className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                      {task.assignees && task.assignees.length > 0 
                                        ? task.assignees.map(a => a.name).join(", ") 
                                        : "Belum Ditugaskan"}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Clock className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                      {task.status === 'todo' ? 'Dibuat ' : task.status === 'in_progress' ? 'Dikerjakan ' : task.status === 'review' ? 'Direview ' : 'Selesai '} 
                                      {new Date(task.updated_at).toLocaleDateString("id-ID", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1.5">
                                  <Badge className={`${PRIORITY_STYLES[task.priority]} text-[10px] px-1.5 py-0 font-medium capitalize`}>
                                    {task.priority}
                                  </Badge>
                                  {task.deadline && (
                                    <div className={`flex items-center gap-1 text-xs ${isOverdue(task) ? "text-red-500 dark:text-red-400 font-medium" : "text-slate-400 dark:text-slate-500"}`}>
                                      {isOverdue(task) ? <AlertTriangle className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                                      {new Date(task.deadline).toLocaleDateString("id-ID", { month: "short", day: "numeric" })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen} task={editTask} onSaved={fetchData} />
    </div>
  );
}
